import { get, toString } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Table } from "antd";
import { useTranslation } from "react-i18next";
import { LoginOutlined } from "@ant-design/icons";
import { useFormik } from "formik";
import { paginationDefaults } from "../../utils/properties";
import Filter from "../../components/functional_components/Filter/Filter";
import { buildQueryString, showError, trimData } from "../../utils/utilities";
import { setFilters } from "../../redux/ducks/Filters";
import { setUserInfo } from "../../redux/ducks/UserInfo";
import Authentication from "../../services/api/Authentication";
import { setLoading } from "../../redux/ducks/Loading";
import User from "../../services/api/User";

const ChooseUser = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const userMaster = useSelector((state) => get(state.userInfoDuck, "userMaster", {}));
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
  });

  const formikFilter = useFormik({
    initialValues: {
      username: "",
      name: "",
      surname: "",
      type: null,
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.type) {
        errors.type = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      values["page"] = 0;
      const queryString = buildQueryString(filters.obj, values);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "input",
      isRequired: false,
      id: "username",
      name: "username",
      value: formikFilter.values.username,
      label: t("chooseUser.filter.username"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "input",
      isRequired: false,
      id: "name",
      name: "name",
      value: formikFilter.values.name,
      label: t("chooseUser.filter.name"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "input",
      isRequired: false,
      id: "surname",
      name: "surname",
      value: formikFilter.values.surname,
      label: t("chooseUser.filter.surname"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "select",
      isRequired: true,
      id: "type",
      name: "type",
      value: formikFilter.values.type,
      label: t("chooseUser.filter.type"),
      onChange: formikFilter.handleChange("type"),
      onBlur: formikFilter.handleBlur("type"),
      dataSelect: [
        { label: "SUPPLIER", value: "SUPPLIER" },
        { label: "INTERNAL", value: "INTERNAL" },
      ],
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString(
      { page: toString(paginationDefaults.page - 1), size: toString(paginationDefaults.size) },
      {}
    );
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  const loginCallback = async (username) => {
    dispatch(setLoading(true));
    const changeUserResponse = await Authentication.changeUser(username);
    if (!changeUserResponse.error) {
      dispatch(setUserInfo(changeUserResponse.data));
      navigate("/");
    } else {
      showError(get(changeUserResponse, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  useEffect(() => {
    dataList(filters.string);
    dispatch(setUserInfo(userMaster));
  }, []);

  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const loginMasterUserResponse = await User.loginMasterUser(queryString);
    if (!loginMasterUserResponse.error && loginMasterUserResponse.data) {
      loginMasterUserResponse.data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(loginMasterUserResponse, "data", []),
        pagination: {
          currentPage: loginMasterUserResponse.currentPage
            ? loginMasterUserResponse.currentPage + 1
            : paginationDefaults.page,
          size: get(loginMasterUserResponse, "pageSize", paginationDefaults.size),
          totalItems: get(loginMasterUserResponse, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(loginMasterUserResponse, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const columns = [
    {
      title: t("chooseUser.table.username"),
      dataIndex: "username",
      key: "username",
    },
    {
      title: t("chooseUser.table.name"),
      dataIndex: "name",
      key: "name",
      responsive: ["md"],
    },
    {
      title: t("chooseUser.table.surname"),
      dataIndex: "surname",
      key: "surname",
      responsive: ["md"],
    },
    {
      title: t("chooseUser.table.type"),
      dataIndex: "type",
      key: "type",
      responsive: ["sm"],
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            <div onClick={() => loginCallback(data.username)}>
              <LoginOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} allInOneRow={true} />
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            const queryString = buildQueryString(filters.obj, { page: toString(page - 1), size: pageSize });
            dispatch(setFilters(queryString));
            dataList(queryString.string);
          },
        }}
      />
    </>
  );
};

export default ChooseUser;
